/* eslint-disable prefer-const */
import { Box, Stack, Text, useToast } from '@chakra-ui/core';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import theme from '../../../../styles/customTheme';
import { FormBuilderSchemaT } from '../types';
import { FORM_COMPONENT_ADVANCED_INPUT_TYPE } from '../schemas/formComponentInputTypeSchema';
import { FORM_ADVANCED_SECTIONS } from '../schemas/formAdvancedSections';
import { hasSectionWithName, isSectionSchema } from '../utils';
import { useStoreActions, useStoreState } from '../../../../models/hooks';

export const SsnIcon = () => (
  <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 0.5H2C0.89 0.5 0.0100002 1.39 0.0100002 2.5L0 14.5C0 15.61 0.89 16.5 2 16.5H18C19.11 16.5 20 15.61 20 14.5V2.5C20 1.39 19.11 0.5 18 0.5ZM18 11.5H16.8L14.25 8V11.5H13V5.5H14.25L16.75 9V5.5H18V11.5ZM2 5.5H6.5V7H3.5V8H6.5V11.5H2V10H5.5V9H2V5.5ZM12 5.5H7.5V9H11V10H7.5V11.5H12V8H9V7H12V5.5Z"
      fill="#586073"
    />
  </svg>
);
export const SinIcon = () => (
  <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2 0.5H18C19.11 0.5 20 1.39 20 2.5V14.5C20 15.61 19.11 16.5 18 16.5H2C0.89 16.5 0 15.61 0 14.5L0.0100002 2.5C0.0100002 1.39 0.89 0.5 2 0.5ZM9 11.5H10.5V5.5H9V11.5ZM16.8 11.5H18V5.5H16.75V9L14.25 5.5H13V11.5H14.25V8L16.8 11.5ZM7 5.5H2V9H5.5V10H2V11.5H7V8H3.5V7H7V5.5Z"
      fill="#586073"
    />
  </svg>
);

interface FormComponentProps {
  component: FormBuilderSchemaT;
}

const FormComponent = React.memo(({ component }: FormComponentProps) => {
  const toast = useToast();
  const { t } = useTranslation('hrFormTemplate');
  const formSchema = useStoreState((state) => state.formBuilder.formSchema);
  const addToCanvas = useStoreActions((actions) => actions.formBuilder.addItemToCanvas);
  const startDrag = useStoreActions((actions) => actions.formBuilder.startDrag);
  const endDrag = useStoreActions((actions) => actions.formBuilder.endDrag);

  let { icon: Icon, label, section: sectionName, type, panel } = component;
  if (component.type === FORM_COMPONENT_ADVANCED_INPUT_TYPE.sinAdvanced) {
    Icon = SinIcon;
  } else if (component.type === FORM_COMPONENT_ADVANCED_INPUT_TYPE.ssnAdvanced) {
    Icon = SsnIcon;
  }
  const isAdvanced = panel === 'advanced';
  const isBasic = panel === 'basic';

  const isSectionSchemaPresent = React.useMemo(
    () => hasSectionWithName(formSchema, sectionName!) && isAdvanced,
    // eslint-disable-next-line
    [formSchema, sectionName],
  );

  const isBankSchemaPresent = React.useMemo(
    () =>
      (sectionName === FORM_ADVANCED_SECTIONS.americanBank || sectionName === FORM_ADVANCED_SECTIONS.canadianBank) &&
      (hasSectionWithName(formSchema, FORM_ADVANCED_SECTIONS.americanBank) ||
        hasSectionWithName(formSchema, FORM_ADVANCED_SECTIONS.canadianBank)),
    [formSchema, sectionName],
  );

  const isSSNOrSINPresent = React.useMemo(
    () =>
      (sectionName === FORM_ADVANCED_SECTIONS.sin || sectionName === FORM_ADVANCED_SECTIONS.ssn) &&
      (hasSectionWithName(formSchema, FORM_ADVANCED_SECTIONS.sin) || hasSectionWithName(formSchema, FORM_ADVANCED_SECTIONS.ssn)),
    [formSchema, sectionName],
  );

  function handleOnClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.preventDefault();
    e.stopPropagation();

    if (isSectionSchemaPresent) {
      toast({
        description: t('formBuilder.componentSidebar.advancedOnceInsertMessage', { component: sectionName?.toUpperCase() }),
        status: 'error',
        isClosable: true,
      });
      return;
    }

    if (isBankSchemaPresent) {
      toast({
        description: t('formBuilder.componentSidebar.onlyOneBankInsertMessage', {
          component1: FORM_ADVANCED_SECTIONS.americanBank,
          component2: FORM_ADVANCED_SECTIONS.canadianBank,
        }),
        status: 'error',
        isClosable: true,
      });
      return;
    }
    if (isSSNOrSINPresent) {
      toast({
        description: t('formBuilder.componentSidebar.onlyOneBankInsertMessage', {
          component1: FORM_ADVANCED_SECTIONS.sin,
          component2: FORM_ADVANCED_SECTIONS.ssn,
        }),
        status: 'error',
        isClosable: true,
      });
      return;
    }

    const schemas = [...formSchema];
    const lastSection = schemas[schemas.length - 1];
    let path = [formSchema.length];
    let parentId: string | null = null;
    if (isSectionSchema(lastSection) && !lastSection.isAdvanced && isBasic) {
      path = [formSchema.length - 1, lastSection.fields.length];
      parentId = lastSection.id;
    }

    addToCanvas({
      draggedItem: {
        id: uuidv4(),
        type: 'paletteItem',
        itemData: type,
      },
      dropTarget: { path, parentId },
    });
  }

  function handleDragStart() {
    startDrag({
      id: uuidv4(),
      type: 'paletteItem',
      itemData: type,
    });
  }

  return (
    <Box
      draggable={!isSectionSchemaPresent && !isBankSchemaPresent && !isSSNOrSINPresent}
      borderRadius={theme.radii.md}
      backgroundColor="#F3F6F9"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      style={
        isSectionSchemaPresent || isBankSchemaPresent || isSSNOrSINPresent
          ? { cursor: 'not-allowed', opacity: 0.6 }
          : { cursor: 'pointer' }
      }
      onClick={handleOnClick}
      onDragStart={handleDragStart}
      onDragEnd={() => endDrag()}
      width="100%"
      height="100%"
      data-testid="form-component-container"
      minHeight={0}
    >
      <Stack spacing={2} align="center">
        <Icon size={24} />
        <Text
          fontWeight={700}
          margin={0}
          textAlign="center"
          isTruncated
          noOfLines={2}
          maxWidth={200}
          sx={{ fontSize: label.length > 20 ? 'xs' : 'sm', lineHeight: label.length > 20 ? '1.1em' : '1.4em' }}
        >
          {t(`formBuilder.componentSidebar.components.${label}`)}
        </Text>
      </Stack>
    </Box>
  );
});

FormComponent.displayName = 'FormComponent';

export default FormComponent;
